/**
 * IdleWarningDialog – warns the user they will be logged out due to inactivity.
 *
 * Displayed when `useIdleTimer` transitions to warning state.  Shows a
 * countdown and two actions:
 *   • "Stay Logged In" — resets the idle timer (primary action).
 *   • "Log Out Now"    — triggers an immediate logout (secondary action).
 *
 * When the countdown reaches 0, the component calls `onLogout` automatically.
 *
 * Security notes
 * ──────────────
 * • The dialog is modal so the user cannot interact with underlying controls
 *   while it is visible, preventing accidental data actions on an
 *   about-to-expire session.
 * • No sensitive data is displayed in the dialog.
 */

import React, { useEffect } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@fluentui/react-components";

export interface IdleWarningDialogProps {
  /** Whether the dialog is visible. */
  isOpen: boolean;
  /** Seconds remaining before auto-logout. */
  remainingSeconds: number;
  /** Called when the user clicks "Stay Logged In". */
  onStayLoggedIn: () => void;
  /** Called when the countdown reaches 0 or the user clicks "Log Out Now". */
  onLogout: () => void;
}

export const IdleWarningDialog: React.FC<IdleWarningDialogProps> = ({
  isOpen,
  remainingSeconds,
  onStayLoggedIn,
  onLogout,
}) => {
  // Auto-logout when the countdown reaches zero.
  useEffect(() => {
    if (isOpen && remainingSeconds <= 0) {
      onLogout();
    }
  }, [isOpen, remainingSeconds, onLogout]);

  return (
    <Dialog
      open={isOpen}
      // Prevent closing via Escape or clicking outside — the user must
      // make an explicit choice (stay or log out).
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Session expiring</DialogTitle>
          <DialogContent>
            <p style={{ margin: 0 }}>
              You've been inactive for a while. For security, you'll be logged out in{" "}
              <strong>
                {remainingSeconds} {remainingSeconds === 1 ? "second" : "seconds"}
              </strong>
              .
            </p>
          </DialogContent>
          <DialogActions>
            {/* Primary action — keeps the user logged in and resets the timer. */}
            <Button appearance="primary" onClick={onStayLoggedIn}>
              Stay Logged In
            </Button>
            {/* Secondary action — logs out immediately. */}
            <Button appearance="secondary" onClick={onLogout}>
              Log Out Now
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
